import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { highlightMatch } from "../../../lib/highlightMatch";
import { useAutocompleteNavigation } from "../../../hooks/useAutocompleteNavigation";

// List of US states with their codes
export const states = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" }
];

export const StateAutocomplete = ({
  item,
  fieldValue,
  onFieldChange,
  error = false,
  helperText,
  disabled = false,
  className,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");

  const inputRef = useRef(null);
  const isOptionSelectedRef = useRef(false);

  // Filter states based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestions([]);
      setIsOpen(false);
      setSearchQuery("");
      return;
    }

    const filteredStates = states.filter((state) =>
      state.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      state.code.toLowerCase().includes(inputValue.toLowerCase())
    );
    
    setSuggestions(filteredStates);
    setIsOpen(filteredStates.length > 0);
    setSelectedIndex(-1);
    setSearchQuery(inputValue);
  }, [inputValue]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    isOptionSelectedRef.current = false;
    // Only clear the field value when input is empty
    if (!newValue.trim()) {
      onFieldChange(item.pathToValue, "", item.type, item);
    }
  };

  const handleClear = () => {
    onFieldChange(item.pathToValue, "", item.type, item);
    setInputValue("");
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    setSearchQuery("");
    inputRef.current?.focus();
  };

  const handleSelect = (state) => {
    isOptionSelectedRef.current = true;
    onFieldChange(item.pathToValue, state.code, item.type, item);
    setInputValue(`${state.name} - ${state.code}`);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const { handleKeyDown, suggestionRefs, containerRef } =
    useAutocompleteNavigation({
      isOpen,
      setIsOpen,
      suggestions,
      selectedIndex,
      setSelectedIndex,
      onSelect: handleSelect,
    });

  const shouldClearOnBlur = item?.clearOnBlur ?? false;
  const handleBlur = () => {
  if (!shouldClearOnBlur) return;
  if (!isOptionSelectedRef.current) {
    setInputValue("");
    onFieldChange(item.pathToValue, "", item.type, item);
  }
  isOptionSelectedRef.current = false;
};


  // Set initial value when fieldValue changes
  useEffect(() => {
    if (fieldValue) {
      const selectedState = states.find(state => state.code === fieldValue);
      if (selectedState) {
        setInputValue(`${selectedState.name} - ${selectedState.code}`);
      }
    } else if (fieldValue === "" || fieldValue === null || fieldValue === undefined) {
      setInputValue("");
    }
  }, [fieldValue]);

  const displayValue = inputValue || "";

  return (
    <div className="space-y-2">
      <div ref={containerRef} className="relative w-full">
        <div className="relative">
          {/* Search Icon */}
          <Search
            className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none ${
              disabled ? "text-gray-300" : "text-gray-400"
            }`}
          />

          {/* Input Field */}
          <input
            ref={inputRef}
            id={item?.pathToValue}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={item?.placeholder || "Search for state"}
            disabled={item?.disabled || disabled}
            autoComplete="off"
            className={`w-full rounded-lg border py-2 pl-10 pr-10 text-sm transition-colors focus:outline-none focus:ring-2 ${
              error
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : disabled
                ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            } ${className || ""}`}
            role="combobox"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-invalid={error ? "true" : "false"}
            aria-describedby={
              helperText || error ? `${item?.pathToValue}-helper` : undefined
            }
            onBlur={handleBlur}
          />

          {/* Clear Button */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {displayValue && !disabled ? (
              <button
                onClick={handleClear}
                className="rounded p-0.5 text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Clear input"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && suggestions.length > 0 && !disabled && (
          <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg">
            <ul role="listbox" className="py-1">
              {suggestions.map((state, index) => {
                const displayText = `${state.name} - ${state.code}`;

                return (
                  <li
                    key={state.code}
                    ref={(el) => (suggestionRefs.current[index] = el)}
                    role="option"
                    aria-selected={selectedIndex === index}
                    className={`cursor-pointer px-3 py-2 transition-colors ${
                      selectedIndex === index
                        ? "bg-[#F7F6F3] text-gray-900"
                        : "text-gray-700 hover:bg-[#F7F6F3]"
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(state);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="text-gray-900 text-base leading-relaxed">
                      {highlightMatch(displayText, searchQuery)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
